import { FormDocument, FormElement } from './store/types';

// Input elements that a master field can be bound to.
export const FILLABLE_TYPES = new Set(['textbox', 'textarea', 'date', 'checkbox', 'radio']);

export function collectVariableFieldKeys(doc: FormDocument): string[] {
  const keys = new Set<string>();
  doc.pages.forEach((page) =>
    page.elements.forEach((el) => {
      (el.content || []).forEach((run) => {
        if (run.type === 'variable') keys.add(run.fieldKey);
      });
      if (el.bindings?.dynamicField) keys.add(el.bindings.dynamicField);
      const table = el.typeProps?.table;
      if (table) {
        table.cells.forEach((row) => row.forEach((cell) => (cell || []).forEach((run) => {
          if (run.type === 'variable') keys.add(run.fieldKey);
        })));
      }
    })
  );
  return Array.from(keys);
}

export interface TemplateFieldInfo {
  pageId: string;
  pageNumber: number;
  element: FormElement;
  // Every element that shares this field: a Yes/No pair is ONE field bound to both boxes
  members: FormElement[];
  hint: string;
}

const YES_NO = new Set(['yes', 'no', 'y', 'n']);
const isOption = (el: FormElement) => el.type === 'checkbox' || el.type === 'radio';
const ownLabel = (el: FormElement) => (el.typeProps?.checkbox?.label ?? el.typeProps?.radio?.label ?? '').trim().toLowerCase();
const yesNoToken = (el: FormElement): string => {
  if (el.type === 'checkbox' || el.type === 'radio') return '';
  const t = (el.content || []).map((r) => (r.type === 'text' ? r.value : '')).join('').trim().toLowerCase().replace(/[\s:/.\-]+$/g, '').replace(/^[\s/]+/, '');
  return YES_NO.has(t) ? t : '';
};
const LABEL_GAP = 28;

// A checkbox's Yes/No label is either its own label or a short "Y"/"No" text glued to it
// ("[ ] Y [ ] N" or "Y [ ] N [ ]"). Each such text belongs to the closest checkbox (ties: the one before it).
function optionLabel(el: FormElement, siblings: FormElement[]): string {
  const own = ownLabel(el);
  if (own) return own;
  for (const t of siblings) {
    const token = yesNoToken(t);
    if (!token || Math.abs(t.y + t.height / 2 - (el.y + el.height / 2)) > 8) continue;
    const gapOf = (c: FormElement) => (c.x + c.width <= t.x + 2 ? t.x - (c.x + c.width) : c.x - (t.x + t.width));
    let best: FormElement | null = null;
    let bestGap = Infinity;
    for (const c of siblings) {
      if (!isOption(c) || ownLabel(c) || Math.abs(t.y + t.height / 2 - (c.y + c.height / 2)) > 8) continue;
      const g = gapOf(c);
      if (g > LABEL_GAP) continue;
      const before = c.x + c.width <= t.x + 2;
      const score = g + (before ? 0 : 0.5);
      if (score < bestGap) {
        bestGap = score;
        best = c;
      }
    }
    if (best && best.id === el.id) return token;
  }
  return '';
}

// A Yes/No (Y/N) pair sitting side by side on one row answers a single question, so it is one field.
export function findYesNoGroup(el: FormElement, siblings: FormElement[]): FormElement[] {
  if (!isOption(el) || !YES_NO.has(optionLabel(el, siblings))) return [el];
  const midY = el.y + el.height / 2;
  const labelOf = new Map<string, string>();
  const row = siblings
    .filter((s) => {
      if (!isOption(s) || Math.abs(s.y + s.height / 2 - midY) >= 6) return false;
      const l = optionLabel(s, siblings);
      labelOf.set(s.id, l);
      return YES_NO.has(l);
    })
    .sort((a, b) => a.x - b.x);
  const optionLabel2 = (s: FormElement) => labelOf.get(s.id) || '';
  const at = row.findIndex((s) => s.id === el.id);
  if (at < 0) return [el];
  let lo = at;
  let hi = at;
  while (lo > 0 && row[lo].x - (row[lo - 1].x + row[lo - 1].width) < 100) lo--;
  while (hi < row.length - 1 && row[hi + 1].x - (row[hi].x + row[hi].width) < 100) hi++;
  const group = row.slice(lo, hi + 1);
  const labels = new Set(group.map(optionLabel2));
  const hasYes = labels.has('yes') || labels.has('y');
  const hasNo = labels.has('no') || labels.has('n');
  return hasYes && hasNo ? group : [el];
}

function plainText(el: FormElement): string {
  return (el.content || [])
    .map((r) => (r.type === 'text' ? r.value : ''))
    .join('')
    .replace(/[_:]+/g, ' ')
    .trim();
}

// Best-effort caption for a blank field: the closest text on the same line to its left,
// otherwise the closest text sitting just above it.
function guessHint(el: FormElement, siblings: FormElement[]): string {
  // symbol-only text ('$', '%', '<') is not a name: 'Individual Maximum $ [ ]' is named by 'Individual Maximum'
  const labels = siblings.filter((s) => s.id !== el.id && !FILLABLE_TYPES.has(s.type) && /[A-Za-z]/.test(plainText(s)) && !yesNoToken(s));
  const midY = el.y + el.height / 2;
  // Imported label widths are estimates and often run a little under the input they name,
  // so a label that STARTS left of the input (and overlaps it only slightly) still counts.
  const overlapOk = Math.min(el.width * 0.5, 40);
  const left = labels
    .filter((s) => s.x < el.x - 2 && s.x + s.width <= el.x + overlapOk && midY >= s.y - 4 && midY <= s.y + s.height + 4)
    .sort((a, b) => b.x - a.x)[0];
  if (left) return plainText(left);
  const above = labels
    .filter((s) => s.y + s.height <= el.y + 4 && el.y - (s.y + s.height) < 30 && s.x <= el.x + el.width && s.x + s.width >= el.x)
    .sort((a, b) => b.y - a.y)[0];
  return above ? plainText(above) : '';
}

// Every blank input on the template (what an upload produces), in reading order.
export function collectTemplateFields(doc: FormDocument): TemplateFieldInfo[] {
  const out: TemplateFieldInfo[] = [];
  doc.pages.forEach((page, pi) => {
    page.elements
      .filter((el) => FILLABLE_TYPES.has(el.type) && !el.locked && el.visible !== false)
      .sort((a, b) => a.y - b.y || a.x - b.x)
      .forEach((el) => {
        const members = findYesNoGroup(el, page.elements);
        if (members[0].id !== el.id) return; // the rest of a Yes/No pair rides on its first box
        out.push({ pageId: page.id, pageNumber: pi + 1, element: el, members, hint: guessHint(el, page.elements) });
      });
  });
  return out;
}
